// frontend/src/utils/geminiIntegration.js

/**
 * Generates a structured prompt for Gemini analysis
 * @param {Object} article - The article object
 * @returns {string} - The formatted prompt for Gemini
 */
export const generateGeminiPrompt = (article) => {
  if (!article) return '';
  
  const {
    title,
    url,
    source_name,
    publication_date,
    classification,
    authors,
    estimated_read_time
  } = article;

  const summary = classification?.summary || "Summary not available.";
  const topics = classification?.topics || [];
  const technicalScore = classification?.technical_score;
  const contentTypes = classification?.content_types || [];
  const targetAudiences = classification?.target_audiences || [];
  
  // Create a comprehensive prompt for Gemini
  let prompt = `Please analyze this article and provide insights:\n\n`;
  prompt += `**Title:** ${title}\n`;
  prompt += `**Source:** ${source_name || 'Unknown'}`;
  
  if (publication_date) {
    const date = new Date(publication_date);
    if (!isNaN(date.getTime())) {
      prompt += ` | **Date:** ${date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`;
    }
  }
  
  if (authors && authors.length > 0) {
    prompt += ` | **Authors:** ${authors.join(', ')}`;
  }
  
  if (estimated_read_time) {
    prompt += ` | **Read Time:** ${estimated_read_time}`;
  }
  
  prompt += `\n\n**Summary:**\n${summary}\n`;
  
  if (topics.length > 0) {
    prompt += `\n**Topics:** ${topics.join(', ')}\n`;
  }
  
  if (technicalScore !== null && technicalScore !== undefined) {
    prompt += `\n**Technical Complexity:** ${technicalScore}/5\n`;
  }
  
  if (contentTypes.length > 0) {
    prompt += `\n**Content Types:** ${contentTypes.join(', ')}\n`;
  }
  
  if (targetAudiences.length > 0) {
    prompt += `\n**Target Audiences:** ${targetAudiences.join(', ')}\n`;
  }
  
  if (url) {
    prompt += `\n**Original Article:** ${url}\n`;
  }
  
  prompt += `\n---\n\nBased on this article, please provide:\n`;
  prompt += `1. Key insights and takeaways\n`;
  prompt += `2. Potential implications or impact\n`;
  prompt += `3. Related topics worth exploring\n`;
  prompt += `4. Questions for further discussion\n`;
  
  return prompt;
};

/**
 * Copies content to clipboard and optionally opens Gemini
 * @param {Object} article - The article object
 * @param {boolean} openGemini - Whether to open Gemini after copying
 * @returns {Promise<boolean>} - Whether the operation was successful
 */
export const copyArticleForAnalysis = async (article, openGemini = true) => {
  const prompt = generateGeminiPrompt(article);
  
  if (!prompt) {
    throw new Error('No article content available to analyze.');
  }
  
  try {
    // Copy the prompt to clipboard
    await navigator.clipboard.writeText(prompt);
    
    // Open Gemini in a new tab if requested
    if (openGemini) {
      window.open('https://gemini.google.com/', '_blank', 'noopener,noreferrer');
      
      // Show success notification only when opening Gemini
      const showNotification = () => {
        // Create a temporary notification
        const notification = document.createElement('div');
        notification.innerHTML = `
          <div style="
            position: fixed;
            top: 20px;
            right: 20px;
            background: #10B981;
            color: white;
            padding: 16px 24px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            z-index: 10000;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            font-size: 14px;
            max-width: 400px;
            animation: slideIn 0.3s ease-out;
          ">
            <div style="display: flex; align-items: center; gap: 8px;">
              <svg style="width: 20px; height: 20px; flex-shrink: 0;" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
              </svg>
              <div>
                <div style="font-weight: 600; margin-bottom: 4px;">Article copied to clipboard!</div>
                <div style="font-size: 12px; opacity: 0.9;">Paste it in Gemini to start analysis</div>
              </div>
            </div>
          </div>
        `;
        
        // Add animation styles
        const style = document.createElement('style');
        style.textContent = `
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
          @keyframes slideOut {
            from { transform: translateX(0); opacity: 1; }
            to { transform: translateX(100%); opacity: 0; }
          }
        `;
        document.head.appendChild(style);
        
        document.body.appendChild(notification);
        
        // Remove notification after 4 seconds
        setTimeout(() => {
          notification.style.animation = 'slideOut 0.3s ease-in forwards';
          setTimeout(() => {
            if (notification.parentNode) {
              notification.parentNode.removeChild(notification);
            }
            if (style.parentNode) {
              style.parentNode.removeChild(style);
            }
          }, 300);
        }, 4000);
      };
      
      showNotification();
    }
    
    return true;
    
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    
    if (openGemini) {
      // Fallback: show the content in an alert for manual copying
      alert(`Gemini will open in a new tab. Please copy and paste this content:\n\n${prompt}`);
      window.open('https://gemini.google.com/', '_blank', 'noopener,noreferrer');
    }
    throw error;
  }
};

// Keep the old function for backward compatibility
export const openGeminiWithArticle = async (article) => {
  return copyArticleForAnalysis(article, true);
};
